import { router } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, Easing, Modal, Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { colors, radius, spacing } from '@/theme/tokens';

// Height of the expo-router tab bar content (without the bottom safe inset).
const TAB_BAR_HEIGHT = 49;

// Quick-action tiles (mock: szybkie_akcje_arkusz_dodawania — Napper's grid of
// big circular actions). Route strings match existing screens.
const ACTIONS: { icon: keyof typeof Ionicons.glyphMap; labelKey: string; route: string; bg: string; fg: string }[] = [
  { icon: 'cart-outline', labelKey: 'fab.expense', route: '/add', bg: '#FDE8E8', fg: '#C0392B' },
  { icon: 'flag-outline', labelKey: 'fab.goal', route: '/goals', bg: '#DCEBFB', fg: '#208AEF' },
  { icon: 'trending-up-outline', labelKey: 'fab.income', route: '/income', bg: '#E4F4EA', fg: '#3BB273' },
  { icon: 'receipt-outline', labelKey: 'fab.fixedCost', route: '/fixed-costs', bg: '#FBEEDC', fg: '#B4562A' },
  { icon: 'sparkles-outline', labelKey: 'fab.scan', route: '/policy/new?mode=scan', bg: '#EDEAFB', fg: '#6D5BD0' },
  { icon: 'notifications-outline', labelKey: 'fab.reminder', route: '/reminder-new', bg: '#E8ECF1', fg: '#0B2C4D' },
];

/**
 * Global "+" — opens the quick-action sheet ("Nowa akcja"): expense, goal
 * contribution, income, fixed cost, policy scan, reminder. Replaced the
 * direct jump to /add once the app grew more things worth adding.
 */
// The sheet starts this far below its resting position and slides up into place.
const SHEET_OFFSET = 600;

export function AddExpenseFab() {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const [open, setOpen] = useState(false);

  // Owner: the scrim must DIM FIRST, then the panel slides up (previously the
  // whole modal slid in together, so the dark strip visibly rode up with it).
  // We drive the two independently: backdrop opacity, then the sheet translateY.
  const backdrop = useRef(new Animated.Value(0)).current;
  const sheetY = useRef(new Animated.Value(SHEET_OFFSET)).current;

  useEffect(() => {
    if (!open) return;
    backdrop.setValue(0);
    sheetY.setValue(SHEET_OFFSET);
    Animated.sequence([
      Animated.timing(backdrop, { toValue: 1, duration: 150, useNativeDriver: true }),
      Animated.timing(sheetY, { toValue: 0, duration: 240, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
    ]).start();
  }, [open, backdrop, sheetY]);

  const close = () => {
    Animated.parallel([
      Animated.timing(sheetY, { toValue: SHEET_OFFSET, duration: 180, easing: Easing.in(Easing.cubic), useNativeDriver: true }),
      Animated.timing(backdrop, { toValue: 0, duration: 180, useNativeDriver: true }),
    ]).start(() => setOpen(false));
  };

  const go = (route: string) => {
    setOpen(false);
    router.push(route as never);
  };

  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('home.addExpense')}
        onPress={() => setOpen(true)}
        style={({ pressed }) => ({
          position: 'absolute',
          right: spacing.screen,
          bottom: TAB_BAR_HEIGHT + insets.bottom + spacing.md,
          width: 56,
          height: 56,
          borderRadius: radius.full,
          backgroundColor: pressed ? colors.primaryPressed : colors.primary,
          alignItems: 'center',
          justifyContent: 'center',
          // stronger than shadows.card — the FAB must read as floating
          shadowColor: colors.navy,
          shadowOpacity: 0.25,
          shadowRadius: 10,
          shadowOffset: { width: 0, height: 4 },
          elevation: 6,
        })}
      >
        <Ionicons name="add" size={30} color="#FFFFFF" />
      </Pressable>

      <Modal visible={open} transparent animationType="none" onRequestClose={close}>
        <View style={{ flex: 1, justifyContent: 'flex-end' }}>
          {/* Scrim fades in first; tapping it closes the sheet. */}
          <Animated.View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(11,44,77,0.45)',
              opacity: backdrop,
            }}
          >
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('common.cancel')}
              onPress={close}
              style={{ flex: 1 }}
            />
          </Animated.View>
          {/* Sheet slides up after the scrim has dimmed. */}
          <Animated.View
            style={{
              backgroundColor: colors.background,
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              padding: spacing.screen,
              paddingBottom: insets.bottom + spacing.lg,
              gap: spacing.md,
              transform: [{ translateY: sheetY }],
            }}
          >
          <View style={{ alignSelf: 'center', width: 44, height: 5, borderRadius: 3, backgroundColor: colors.outlineVariant }} />
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <AppText variant="headlineMd" style={{ flex: 1 }}>
              {t('fab.title')}
            </AppText>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('common.cancel')}
              onPress={close}
              style={{ minWidth: 40, minHeight: 40, alignItems: 'center', justifyContent: 'center' }}
            >
              <Ionicons name="close" size={22} color={colors.navy} />
            </Pressable>
          </View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
            {ACTIONS.map(({ icon, labelKey, route, bg, fg }) => (
              <Pressable
                key={labelKey}
                accessibilityRole="button"
                onPress={() => go(route)}
                style={{ width: '33.33%', alignItems: 'center', gap: spacing.xs, paddingVertical: spacing.md }}
              >
                <View
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: 32,
                    backgroundColor: bg,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Ionicons name={icon} size={26} color={fg} />
                </View>
                <AppText variant="labelLg" style={{ textAlign: 'center' }}>
                  {t(labelKey)}
                </AppText>
              </Pressable>
            ))}
          </View>
          </Animated.View>
        </View>
      </Modal>
    </>
  );
}
